import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clx from 'classnames';
import { Layers, Flame, Rocket, ChevronDown } from 'lucide-react';
import styles from './PlanButton.module.scss';
import headerActionStyles from '../HeaderActionButtons.module.scss';
import PlanPanel from '../PlanPanel';
import { getPlanLabels } from '../../utils/getActivePlan';
import useActivePlan from 'utils/useActivePlan';
import useWindowSize from 'utils/useWindowSize';
import useOutsideClick from 'utils/useOutsideClick';
import { getActiveSalon } from 'Containers/Auth/store/selectors';
import { modalActions } from 'Containers/Modals/store';
import { goToBilling, goToPremiumUpgrade } from 'utils/premiumUpgradePath';

const PLAN_ICONS = {
  layers: Layers,
  flame: Flame,
  rocket: Rocket,
};

const PlanButton = ({ showDivider = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const activeSalon = useSelector(getActiveSalon());
  const plan = useActivePlan();
  const planLabels = useMemo(() => getPlanLabels(plan.id, t), [plan.id, t]);
  const PlanIcon = PLAN_ICONS[plan.icon];

  useOutsideClick(dropdownRef, () => {
    if (!isMobile && isDropdownOpen) setIsDropdownOpen(false);
  });

  const handleClick = () => {
    if (isMobile) {
      dispatch(modalActions.OPEN_MODAL.SUCCESS({ type: 'planModal' }));
      return;
    }
    setIsDropdownOpen((open) => !open);
  };

  const handleUpgradeClick = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    // Sempre la landing: non dipendere da plan.id (il CTA è già "Passa a Premium").
    // Chiudi il dropdown solo dopo la navigate (timing gestito in goToPremiumUpgrade).
    goToPremiumUpgrade(navigate, {
      onAfterNavigate: () => setIsDropdownOpen(false),
    });
  };

  const handleManageClick = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    goToBilling(navigate, {
      onAfterNavigate: () => setIsDropdownOpen(false),
    });
  };

  return (
    <div
      ref={dropdownRef}
      className={clx(styles.plan_selector, {
        [styles.plan_selector_close]: !isDropdownOpen || isMobile,
      })}
    >
      {showDivider ? (
        <span className={headerActionStyles.actions_divider} aria-hidden="true" />
      ) : null}
      <button
        type="button"
        className={clx(
          styles.plan_button,
          styles[`plan_button_${plan.id}`],
          {
            [styles.plan_button_mobile]: isMobile,
            [styles.plan_button_open]: isDropdownOpen && !isMobile,
          },
        )}
        onClick={handleClick}
        aria-expanded={!isMobile && isDropdownOpen}
        aria-haspopup={!isMobile ? 'dialog' : undefined}
        aria-label={t('header.plan.ariaOpen', { plan: planLabels.label })}
      >
        {PlanIcon ? (
          <PlanIcon
            className={styles.plan_icon}
            size={16}
            strokeWidth={plan.icon === 'flame' ? 2.25 : 2}
            fill={plan.icon === 'flame' ? 'currentColor' : 'none'}
          />
        ) : null}
        {isMobile ? (
          <span className={styles.plan_name}>{planLabels.shortLabel}</span>
        ) : (
          <span className={styles.plan_text}>
            <span className={styles.plan_eyebrow}>{t('header.plan.currentPlan')}</span>
            <span className={styles.plan_name_row}>
              <span className={styles.plan_name}>{planLabels.shortLabel}</span>
              <span className={styles.plan_status}>{t('header.plan.active')}</span>
            </span>
          </span>
        )}
        {!isMobile ? (
          <ChevronDown
            className={styles.plan_chevron}
            size={14}
            strokeWidth={2.5}
            aria-hidden="true"
          />
        ) : null}
      </button>
      {!isMobile && (
        <div className={styles.dropdown__menu}>
          <div className={styles.dropdown__menu_inner_list}>
            <PlanPanel
              plan={plan}
              salon={activeSalon}
              compact
              onUpgradeClick={handleUpgradeClick}
              onManageClick={handleManageClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanButton;
